/* eslint-disable @typescript-eslint/ban-ts-comment */
import ProjectRole, { ProjectMemberRoleJSON, ProjectMemberRoleType } from '@projectMembers/models/projectRole';
import User, { UserJson } from 'src/new_arch/models/user/user';

type EditorAccessStateJSON = {
	state: 'pending' | 'trial' | 'full' | 'limited' | 'grace_period_expired';
	days_remaining?: number;
	modal_seen?: boolean;
} | null;

export type EditorAccessState = {
	state: 'pending' | 'trial' | 'full' | 'limited' | 'grace_period_expired';
	daysRemaining?: number;
	modalSeen?: boolean;
} | null;

export type ProjectMemberJSON = {
	id: number,
	user: UserJson,
	role: ProjectMemberRoleJSON,
	editor_access_state?: EditorAccessStateJSON,
	is_limited_editor?: boolean
};

export type ProjectMemberParams = {
	id: number,
	user: User,
	roleType: ProjectMemberRoleType,
	editorAccessState?: EditorAccessState
}

export default class ProjectMember {
	id: number;
	user: User;
	roleType: ProjectMemberRoleType;
	editorAccessState: EditorAccessState;

	constructor( { id, user, roleType, editorAccessState = null }: ProjectMemberParams ) {
		this.id = id;
		this.user = user;
		this.roleType = roleType;
		this.editorAccessState = editorAccessState;
	}

	get role() {
		return ProjectRole.fromID( this.roleType )!;
	}

	get isOwner() {
		return this.roleType === ProjectMemberRoleType.ProjectOwner;
	}

	get isGuest() {
		return this.user.isGuest;
	}

	get isLimitedEditor() {
		return this.roleType === ProjectMemberRoleType.ProjectLimitedEditor;
	}

	get isEditorAccessExpired() {
		return this.editorAccessState?.state === 'limited'
			|| this.editorAccessState?.state === 'grace_period_expired'
			|| ( this.editorAccessState?.daysRemaining != null && this.editorAccessState.daysRemaining <= 0 );
	}

	hasRole( role: ProjectMemberRoleType ) {
		return this.roleType === role;
	}

	static fromJson( json: ProjectMemberJSON ): ProjectMember {
		let editorAccessState: EditorAccessState = null
		if ( json.editor_access_state ) {
			editorAccessState = {
				state: json.editor_access_state.state,
				daysRemaining: json.editor_access_state.days_remaining,
				modalSeen: json.editor_access_state.modal_seen ?? false
			}
		} else if ( json.is_limited_editor ) {
			editorAccessState = { state: 'limited', modalSeen: false }
		}

		return new ProjectMember( {
			id: json.id,
			user: User.fromJson( json.user ),
			roleType: json.role.type,
			editorAccessState
		} );
	}

	toJson(): ProjectMemberJSON {
		return {
			id: this.id,
			user: this.user.toJson(),
			role: {
				id: this.id,
				type: this.roleType
			},
			editor_access_state: this.editorAccessState
		};
	}
}
